/**
 * Procedural rock surface for the asteroid kit (D-047 lighting overhaul; asteroid-set sheet, concept
 * docs/art/concepts/asteroid-set). One TSL material per archetype, instanced-field friendly:
 *
 * - **Height field in the rock's own unit space** (positionGeometry, so it sticks to the rock under instancing, spin
 *   and the floating origin): impact craters from a Worley field (bowls with raised rims), lumpy fractal relief and a
 *   fine grit layer that fades out once it is smaller than a pixel.
 * - **Bump normal:** Mikkelsen's surface-gradient method on screen derivatives of that height, scaled into view space
 *   by the instance scale measured from the same derivatives, so a 3 m and a 30 m rock carry the same relative relief.
 * - **Albedo and roughness:** the archetype colour with per-rock tint (the `aSeed` instance attribute), darker crater
 *   floors and dust-filled cavities, brighter fresh rims; roughness 0.8-0.97 with breakup; M-type veins turn
 *   metallic.
 * - **Cavity AO:** crater floors and relief troughs feed the material AO (indirect light only).
 *
 * `detail = false` is the lean WebGL2 variant (webgl2.json `rockDetail`, D-053): no MaterialX noise at all (ANGLE's
 * FXC took about 1.5 s per rock program to compile it), just the archetype colour, per-rock tint, a cheap analytic
 * mottle and the geometry normal.
 */
import { Color, MeshStandardNodeMaterial, type Node } from 'three/webgpu'
import {
  abs,
  attribute,
  clamp,
  color,
  cross,
  dFdx,
  dFdy,
  dot,
  exp,
  faceDirection,
  float,
  fwidth,
  length,
  max,
  mix,
  mx_fractal_noise_float,
  mx_worley_noise_float,
  normalView,
  normalize,
  positionGeometry,
  positionView,
  saturate,
  sign,
  sin,
  smoothstep,
  vec3,
} from 'three/tsl'
import { ASTEROID_ATTR } from '../contracts'

type F = Node<'float'>
type V3 = Node<'vec3'>

export interface RockLook {
  /** sRGB base colour (concept-driven, readable brown-grey). */
  color: string
  roughness: number
  /** Metalness in the veins (M-type), 0 = none. */
  veinMetal: number
  /** Crater density (cells per unit radius) and depth (fraction of the radius). */
  craterScale: number
  craterDepth: number
  /** Lumpy relief amplitude (fraction of the radius). */
  relief: number
}

/** Mikkelsen surface-gradient bump in view space from a height in metres. */
export function bumpNormal(height: F): V3 {
  const dHdx = dFdx(height)
  const dHdy = dFdy(height)
  const sx = dFdx(positionView)
  const sy = dFdy(positionView)
  const n = normalize(normalView)
  const r1 = cross(sy, n)
  const r2 = cross(n, sx)
  const det = dot(sx, r1).mul(faceDirection)
  const grad = sign(det).mul(1).mul(r1.mul(dHdx).add(r2.mul(dHdy)))
  return normalize(abs(det).mul(n).sub(grad))
}

/** The lean rock (no procedural detail): archetype colour, per-rock tint and a low-frequency analytic mottle. */
function configureLeanRock(m: MeshStandardNodeMaterial, look: RockLook, seed: F, p: V3): MeshStandardNodeMaterial {
  const base = color(new Color(look.color)) as unknown as V3
  const tint = mix(vec3(0.92, 0.9, 0.88), vec3(1.08, 1.04, 1), seed)
  const mottle = sin(p.x.mul(2.3).add(sin(p.y.mul(3.1)))).mul(sin(p.z.mul(2.7).add(p.x))).mul(0.09).add(1)
  m.colorNode = base.mul(tint).mul(mottle)
  m.roughnessNode = float(look.roughness)
  m.metalnessNode = float(look.veinMetal * 0.15)
  return m
}

export function createRockMaterial(id: string, look: RockLook, detail = true): MeshStandardNodeMaterial {
  const m = new MeshStandardNodeMaterial()
  m.name = `rock:${id}`
  const seed = attribute(ASTEROID_ATTR.seed, 'float')
  const p = positionGeometry.add(vec3(seed.mul(19.1), seed.mul(7.3), seed.mul(13.7)))
  if (!detail) return configureLeanRock(m, look, seed, p)
  // Pixel footprint in unit space: fade details that fall under a pixel (no sparkle at range).
  const fp = length(fwidth(positionGeometry))
  const fine = float(1).sub(smoothstep(0.004, 0.02, fp))
  const mid = float(1).sub(smoothstep(0.02, 0.08, fp))

  // Craters: Worley F1 bowls with raised rims, two sizes.
  const craters = (scale: number, depth: number): { h: F; floor: F; rim: F } => {
    const f1 = mx_worley_noise_float(p.mul(scale), 0.9) as unknown as F
    const r = saturate(f1.div(0.55))
    const bowl = r.mul(r).sub(1).mul(depth)
    const rimBand = exp(r.sub(1).mul(r.sub(1)).mul(-60)).mul(depth * 0.35)
    const inside = float(1).sub(smoothstep(0.85, 1, r))
    return { h: bowl.mul(inside).add(rimBand), floor: float(1).sub(r).mul(inside), rim: rimBand.div(depth * 0.35) }
  }
  const big = craters(look.craterScale, look.craterDepth)
  const small = craters(look.craterScale * 3.1, look.craterDepth * 0.45)
  // Crater fields cluster: large patches stay smooth (older regolith), others are heavily pocked.
  const field = smoothstep(0.35, 0.65, mx_fractal_noise_float(p.mul(0.9).add(5.3), 2, 2, 0.5).mul(0.5).add(0.5))
  const smallField = smoothstep(0.25, 0.55, mx_fractal_noise_float(p.mul(1.7).add(11.1), 2, 2, 0.5).mul(0.5).add(0.5))
  const relief = mx_fractal_noise_float(p.mul(2.3), 4, 2, 0.5).mul(look.relief)
  const grit = mx_fractal_noise_float(p.mul(22), 3, 2, 0.5).mul(0.006).mul(fine)
  const hUnit = relief.add(big.h.mul(field)).add(small.h.mul(mid).mul(smallField)).add(grit)

  // Instance scale from matching derivatives: view metres per unit of rock space.
  const scale = length(dFdx(positionView)).div(max(length(dFdx(positionGeometry)), 1e-7))
  m.normalNode = bumpNormal(hUnit.mul(scale))

  // Albedo: per-rock tint, crater floors and troughs darker (dust), fresh rims brighter, low-frequency mottling.
  const base = color(new Color(look.color)) as unknown as V3
  const tint = mix(vec3(0.92, 0.9, 0.88), vec3(1.08, 1.04, 1), seed)
  const mottle = mx_fractal_noise_float(p.mul(1.3), 3, 2, 0.5).mul(0.18).add(1)
  const trough = saturate(relief.div(look.relief).mul(-0.5).add(0.5))
  const cavity = saturate(big.floor.mul(0.8).mul(field).add(small.floor.mul(0.4).mul(mid).mul(smallField)).add(trough.mul(0.35)))
  const albedo = base.mul(tint).mul(mottle).mul(float(1).sub(cavity.mul(0.35))).mul(float(1).add(big.rim.mul(0.25).mul(field)))
  // Veins (M-type): a thin metallic network from a second Worley field.
  const veinF = mx_worley_noise_float(p.mul(4.2).add(3.7), 1) as unknown as F
  const vein = look.veinMetal > 0 ? float(1).sub(smoothstep(0.04, 0.09, veinF)).mul(mid) : float(0)
  m.colorNode = mix(albedo, vec3(0.61, 0.58, 0.54) as V3, vein.mul(0.8))
  const breakup = mx_fractal_noise_float(p.mul(6), 2, 2, 0.5).mul(0.06)
  m.roughnessNode = clamp(float(look.roughness).add(breakup).sub(big.rim.mul(0.05)).sub(vein.mul(0.35)), 0.45, 0.98)
  m.metalnessNode = vein.mul(look.veinMetal)
  m.aoNode = float(1).sub(cavity.mul(0.55))
  return m
}
